import { memo, useMemo, type CSSProperties } from 'react';
import { getTemperatureStyle, getPrecipStyle, getCloudCoverStyle, getWindSpeedStyle } from '../../utils/colorCalculators';
import { formatValue } from '../../utils/dataTransformers';

interface DataCellProps {
  value: number | null | undefined;
  isTemp: boolean;
  isPrecip: boolean;
  isElevation?: boolean;
  isCloudCover?: boolean;
  isWind?: boolean;
  customFormat?: (value: number) => string;
  customStyle?: () => CSSProperties;
  groupInterval?: string;
}

const DataCell = memo(({ 
  value, 
  isTemp, 
  isPrecip, 
  isElevation = false,
  isCloudCover = false,
  isWind = false,
  customFormat,
  customStyle,
  groupInterval
}: DataCellProps) => {
  const cellStyle = useMemo(() => {
    if (customStyle) return customStyle();
    if (isTemp) return getTemperatureStyle(value);
    if (isPrecip) return getPrecipStyle(value, groupInterval);
    if (isCloudCover) return getCloudCoverStyle(value);
    if (isWind) return getWindSpeedStyle(value);
    return { width: '100%', textAlign: 'center' as const };
  }, [value, isTemp, isPrecip, isCloudCover, customStyle]);

  const formattedValue = customFormat 
    ? (value != null ? customFormat(value) : '') 
    : formatValue(value, isElevation, isPrecip);
  
  return (
    <div style={cellStyle}>
      {formattedValue}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value &&
         prevProps.isTemp === nextProps.isTemp &&
         prevProps.isPrecip === nextProps.isPrecip &&
         prevProps.isElevation === nextProps.isElevation &&
         prevProps.isCloudCover === nextProps.isCloudCover &&
         prevProps.isWind === nextProps.isWind &&
         prevProps.customFormat === nextProps.customFormat &&
         prevProps.customStyle === nextProps.customStyle;
});

export default DataCell;
